import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { getDashboardStats } from "../../api/adminService";
import { formatCurrency, formatDate, OrderStatusByName } from "./constants";

const PLATFORM_CARDS = [
  { key: "totalUsers", label: "Clients", icon: PeopleAltOutlinedIcon, color: "primary", path: "/admin/users" },
  {
    key: "totalBusinessOwners",
    label: "Business Owners",
    icon: BusinessCenterOutlinedIcon,
    color: "success",
    path: "/admin/users",
  },
  { key: "totalStores", label: "Stores", icon: StorefrontOutlinedIcon, color: "secondary", path: "/admin/stores" },
  {
    key: "totalDrivers",
    label: "Delivery Drivers",
    icon: DeliveryDiningOutlinedIcon,
    color: "info",
    path: "/admin/delivery",
  },
  {
    key: "totalDeliveryCompanies",
    label: "Delivery Companies",
    icon: DomainOutlinedIcon,
    color: "warning",
    path: "/admin/delivery-companies",
  },
];

const FINANCE_ROWS = [
  {
    label: "Store revenue",
    grossKey: "productRevenue",
    commissionKey: "storeCommissionRevenue",
    netKey: "storeNetRevenue",
    icon: StorefrontOutlinedIcon,
  },
  {
    label: "Delivery revenue",
    grossKey: "deliveryRevenue",
    commissionKey: "deliveryCommissionRevenue",
    netKey: "deliveryNetRevenue",
    icon: DeliveryDiningOutlinedIcon,
  },
];

const STATUS_CHART_COLORS = {
  Pending: "#d97706",
  Confirmed: "#0891b2",
  Preparing: "#0891b2",
  ReadyForPickup: "#056150",
  OutForDelivery: "#113B36",
  Delivered: "#16a34a",
  Cancelled: "#dc2626",
  Refunded: "#94a3b8",
};

function formatDayLabel(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { weekday: "short" });
}

function HeroStat({ icon: Icon, label, value, helper, color, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        p: 2.5,
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": onClick
          ? { transform: "translateY(-2px)", boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)" }
          : undefined,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 800, letterSpacing: "-0.02em" }} noWrap>
            {value}
          </Typography>
          {helper && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              {helper}
            </Typography>
          )}
        </Box>
        <Avatar
          variant="rounded"
          sx={{ bgcolor: `${color}.main`, width: 44, height: 44, borderRadius: 2.5, flexShrink: 0 }}
        >
          <Icon fontSize="small" />
        </Avatar>
      </Stack>
    </Card>
  );
}

function PlatformStat({ icon: Icon, label, value, color, onClick }) {
  return (
    <Paper
      onClick={onClick}
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2.5,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { borderColor: `${color}.main`, bgcolor: "rgba(15,23,42,0.02)" } : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ bgcolor: `${color}.main`, width: 38, height: 38 }}>
          <Icon fontSize="small" />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
            {value}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const PLATFORM_FILTERS = [
  { value: "all", label: "Both", icon: DevicesOutlinedIcon },
  { value: "Web", label: "Website", icon: LanguageOutlinedIcon },
  { value: "Mobile", label: "Mobile App", icon: PhoneIphoneOutlinedIcon },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");

  useEffect(() => {
    let active = true;
    setLoading(true);
    getDashboardStats(platformFilter === "all" ? undefined : platformFilter)
      .then((data) => {
        if (active) setStats(data);
      })
      .catch(() => {
        if (active) setError("Could not load dashboard stats. Is the API running?");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [platformFilter]);

  const revenueTrend = useMemo(() => {
    if (!stats?.revenueLast7Days) return { labels: [], values: [] };
    return {
      labels: stats.revenueLast7Days.map((p) => formatDayLabel(p.date)),
      values: stats.revenueLast7Days.map((p) => p.amount),
    };
  }, [stats]);

  const statusEntries = useMemo(() => {
    if (!stats?.ordersByStatus) return [];
    return Object.entries(stats.ordersByStatus)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [stats]);

  const platformToggle = (
    <ToggleButtonGroup
      value={platformFilter}
      exclusive
      size="small"
      onChange={(e, value) => {
        if (value) setPlatformFilter(value);
      }}
      sx={{ bgcolor: "background.paper" }}
    >
      {PLATFORM_FILTERS.map(({ value, label, icon: Icon }) => (
        <ToggleButton key={value} value={value} sx={{ px: 2, gap: 0.75, textTransform: "none", fontWeight: 700 }}>
          <Icon fontSize="small" />
          {label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            A snapshot of marketplace performance, revenue, and operations.
          </Typography>
        </Box>
        {platformToggle}
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && stats && (
        <>

      {/* Hero KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <HeroStat
            icon={PaidOutlinedIcon}
            label="Total Revenue"
            value={formatCurrency(stats.totalRevenue)}
            helper="All-time, excluding cancelled orders"
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <HeroStat
            icon={ReceiptLongOutlinedIcon}
            label="Total Orders"
            value={stats.totalOrders.toLocaleString()}
            helper={`${stats.ordersToday.toLocaleString()} placed today`}
            color="secondary"
            onClick={() => navigate("/admin/orders")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <HeroStat
            icon={LocalShippingOutlinedIcon}
            label="Active Deliveries"
            value={stats.activeDeliveries.toLocaleString()}
            helper="In progress right now"
            color="info"
            onClick={() => navigate("/admin/delivery")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <HeroStat
            icon={PendingActionsOutlinedIcon}
            label="Pending Store Approvals"
            value={stats.pendingStoreApprovals.toLocaleString()}
            helper="Awaiting review"
            color="warning"
            onClick={() => navigate("/admin/stores")}
          />
        </Grid>
      </Grid>

      {/* Trend + status breakdown */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Revenue, last 7 days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total order value per day, excluding cancelled orders
                </Typography>
              </Box>
            </Stack>
            {revenueTrend.values.length > 0 ? (
              <LineChart
                height={260}
                series={[
                  {
                    data: revenueTrend.values,
                    color: "#12B886",
                    area: true,
                    showMark: true,
                    valueFormatter: (v) => formatCurrency(v),
                  },
                ]}
                xAxis={[{ scaleType: "point", data: revenueTrend.labels }]}
                yAxis={[{ width: 60 }]}
                grid={{ horizontal: true }}
                margin={{ left: 0, right: 10, top: 20, bottom: 20 }}
                sx={{
                  "& .MuiLineElement-root": { strokeWidth: 2 },
                  "& .MuiAreaElement-root": { fillOpacity: 0.12 },
                  "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: "#e2e8f0" },
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                No revenue recorded in the last 7 days.
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Orders by status
            </Typography>
            <Typography variant="caption" color="text.secondary">
              All-time order distribution
            </Typography>

            {statusEntries.length > 0 ? (
              <>
                <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                  <PieChart
                    height={200}
                    margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    series={[
                      {
                        data: statusEntries.map(([name, count]) => ({
                          id: name,
                          value: count,
                          label: OrderStatusByName[name]?.label || name,
                          color: STATUS_CHART_COLORS[name] || "#94a3b8",
                        })),
                        innerRadius: 45,
                        outerRadius: 85,
                        paddingAngle: 2,
                        cornerRadius: 3,
                      },
                    ]}
                    slotProps={{ legend: { hidden: true } }}
                  />
                </Box>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {statusEntries.map(([name, count]) => (
                    <Stack key={name} direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: "50%",
                            bgcolor: STATUS_CHART_COLORS[name] || "#94a3b8",
                          }}
                        />
                        <Typography variant="body2">{OrderStatusByName[name]?.label || name}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={700}>
                        {count.toLocaleString()}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                No orders yet.
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Platform totals */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        Platform totals
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {PLATFORM_CARDS.map(({ key, label, icon, color, path }) => (
          <Grid size={{ xs: 6, sm: 4, lg: 2.4 }} key={key}>
            <PlatformStat
              icon={icon}
              label={label}
              value={stats[key].toLocaleString()}
              color={color}
              onClick={() => navigate(path)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Financial breakdown + recent orders */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Financial breakdown
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Gross revenue, platform commission, and net payout by stream
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              {FINANCE_ROWS.map((row) => (
                <Box key={row.label}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <row.icon fontSize="small" sx={{ color: "text.secondary" }} />
                    <Typography variant="body2" fontWeight={700}>
                      {row.label}
                    </Typography>
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid size={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Gross
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatCurrency(stats[row.grossKey])}
                      </Typography>
                    </Grid>
                    <Grid size={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Commission
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {formatCurrency(stats[row.commissionKey])}
                      </Typography>
                    </Grid>
                    <Grid size={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Net
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatCurrency(stats[row.netKey])}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <LocalOfferOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                  <Typography variant="body2" fontWeight={700}>
                    Coupons
                  </Typography>
                </Stack>
                <Grid container spacing={1}>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Discounts given
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="warning.main">
                      {formatCurrency(stats.discountTotal)}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Free delivery cost
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="warning.main">
                      {formatCurrency(stats.freeDeliveryCost)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Recent orders
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                onClick={() => navigate("/admin/orders")}
                sx={{ cursor: "pointer", color: "secondary.main", "&:hover": { textDecoration: "underline" } }}
              >
                <Typography variant="body2" fontWeight={700} color="inherit">
                  View all
                </Typography>
                <ArrowForwardIosRoundedIcon sx={{ fontSize: 11 }} />
              </Stack>
            </Stack>

            {stats.recentOrders?.length > 0 ? (
              <Stack divider={<Divider />} sx={{ mt: 1 }}>
                {stats.recentOrders.map((order) => {
                  const statusInfo = OrderStatusByName[order.status] || { label: order.status, color: "default" };
                  return (
                    <Stack
                      key={order.orderId}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        py: 1.5,
                        cursor: "pointer",
                        borderRadius: 1.5,
                        "&:hover": { bgcolor: "rgba(15,23,42,0.03)" },
                      }}
                      onClick={() => navigate(`/admin/orders/${order.orderId}`)}
                    >
                      <Avatar
                        src={order.storeLogoUrl || undefined}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: "secondary.light" }}
                      >
                        <StorefrontOutlinedIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {order.orderNumber}
                          </Typography>
                          <Chip
                            label={statusInfo.label}
                            size="small"
                            color={statusInfo.color}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                          {order.storeName} &middot; {order.customerName} &middot; {formatDate(order.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0 }}>
                        {formatCurrency(order.totalAmount)}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                No orders yet.
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>
        </>
      )}
    </Box>
  );
}
